import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import DataTable, { Column } from '../../components/DataTable/DataTable';
import { CrewDataTableFrame, CrewDataTablePrompt, ReportTextCell, CrewReportViewAction, useCrewReportOpen } from './reportShared';
import { DateInput, VesselSelect } from '../../components/ui';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import CrewReportSearchHeader, { CrewReportFilterField } from './CrewReportSearchHeader';
import type { RightShipMatrixResponse, RightShipMatrixRow } from './types';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

const TextCell: React.FC<{ value: string | number | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  const text = value == null || value === '' ? '-' : String(value);
  return <span className={cn('text-sm whitespace-nowrap', colors.text)}>{text}</span>;
};

const RightShipMatrixPage: React.FC = () => {
  const { colors } = useTheme();
  const { listReturnPath, openCrew } = useCrewReportOpen();
  const [vesselId, setVesselId] = useState('');
  const [periodFrom, setPeriodFrom] = useState(todayIso);
  const [periodTo, setPeriodTo] = useState(todayIso);
  const [applied, setApplied] = useState<{ vesselId: string; periodFrom: string; periodTo: string } | null>(
    null,
  );

  const listUrl = applied
    ? `/crew/rightship-matrix?vesselId=${encodeURIComponent(applied.vesselId)}&periodFrom=${encodeURIComponent(applied.periodFrom)}&periodTo=${encodeURIComponent(applied.periodTo)}`
    : null;

  const { data, error, isLoading, mutate } = useSWR<RightShipMatrixResponse>(listUrl, fetcher, {
    revalidateOnFocus: false});

  const handleSearch = useCallback(() => {
    if (!vesselId) return;
    setApplied({ vesselId, periodFrom, periodTo });
  }, [vesselId, periodFrom, periodTo]);

  const rows = data?.rows ?? [];
  const vessel = data?.vessel;

  const columns: Column<RightShipMatrixRow>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Name',
        sortable: true,
        render: (_v, row) => (
          <Link
            to={`/crew/${row.crewId}`}
            state={{ from: listReturnPath }}
            className={cn('text-sm hover:underline whitespace-nowrap', colors.primaryFg)}
            onClick={(e) => e.stopPropagation()}
          >
            {row.name}
          </Link>
        )},
      { key: 'rank', header: 'Rank', sortable: true, render: (v) => <TextCell value={v} /> },
      { key: 'nation', header: 'Nation', sortable: true, render: (v) => <TextCell value={v} /> },
      {
        key: 'timeInRankYears',
        header: 'Time in rank (years)',
        sortable: true,
        render: (v) => <TextCell value={v} />},
      {
        key: 'timeWithCompanyYears',
        header: 'Time with company (years)',
        sortable: true,
        render: (v) => <TextCell value={v} />},
      {
        key: 'timeOnShipTypeYears',
        header: 'Time on ship type (years)',
        sortable: true,
        render: (v) => <TextCell value={v} />},
      {
        key: 'timeWithMainEngineYears',
        header: 'Time with this model of main engine (years)',
        sortable: true,
        render: (v) => <TextCell value={v} />},
      {
        key: 'timeOnOtherShipTypesYears',
        header: 'Time on other ship types (years)',
        sortable: true,
        render: (v) => <TextCell value={v} />},
      {
        key: 'timeOnboardThisTourMonths',
        header: 'Time onboard this tour (months)',
        sortable: true,
        render: (v) => <TextCell value={v} />},
    ],
    [],
  );

  return (
    <div className="h-full flex flex-col min-h-0 gap-3">
      <CrewReportSearchHeader
        hint={{
          title: 'RightShip Officers Qualifications Matrix',
          body: 'RightShip-oriented tenure metrics for officers on the vessel: time in rank, with company, on ship type, on main engine model, and months onboard this tour. Select vessel and period, then search.'}}
        onSearch={handleSearch}
        searchDisabled={!vesselId}
        loading={isLoading}
        onRefresh={applied ? () => mutate() : undefined}
        context={
          vessel
            ? `RightShip matrix · ${vessel.vesselName}${vessel.vesselAbrv ? ` (${vessel.vesselAbrv})` : ''}${vessel.mainEngType ? ` · Engine: ${vessel.mainEngType}` : ''} · ${applied?.periodFrom} → ${applied?.periodTo}`
            : undefined
        }
      >
        <CrewReportFilterField label="Vessel" htmlFor="rightship-matrix-vessel" width="lg">
          <VesselSelect
            name="vesselId"
            value={vesselId}
            onChange={(e) => setVesselId(String(e.target.value))}
            placeholder="Select vessel…"
            includeIds={vesselId ? [vesselId] : undefined}
          />
        </CrewReportFilterField>
        <CrewReportFilterField label="Period from" htmlFor="rightship-matrix-from" width="md">
          <DateInput
            id="rightship-matrix-from"
            name="periodFrom"
            value={periodFrom}
            onChange={(e) => setPeriodFrom(e.target.value)}
          />
        </CrewReportFilterField>
        <CrewReportFilterField label="Period to" htmlFor="rightship-matrix-to" width="md">
          <DateInput
            id="rightship-matrix-to"
            name="periodTo"
            value={periodTo}
            onChange={(e) => setPeriodTo(e.target.value)}
          />
        </CrewReportFilterField>
      </CrewReportSearchHeader>

      <CrewDataTableFrame>
        {!applied ? (
          <CrewDataTablePrompt>Select a vessel and period, then search.
          </CrewDataTablePrompt>
        ) : error ? (
          <CrewDataTablePrompt><span className="text-red-600">
            Could not load RightShip matrix.
          </span></CrewDataTablePrompt>
        ) : (
          <DataTable<RightShipMatrixRow>
            data={rows}
            columns={columns}
            keyExtractor={(row) => row.transactionId}
            loading={isLoading}
            emptyMessage="No data to display"
            onRowClick={(row) => openCrew(row.crewId)}
            actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
            actionsPosition="left"
          />
        )}
      </CrewDataTableFrame>

      {applied && data?.summary && (
        <div
          className={cn(
            'flex flex-wrap items-center gap-6 rounded-xl border px-4 py-3 text-sm shadow-sm',
            colors.surface,
            colors.border,
          )}
        >
          <div>
            <span className={colors.textSecondary}>Officers</span>
            <span className={cn('ml-2 font-semibold', colors.text)}>{data.summary.crewCount}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default RightShipMatrixPage;
